/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { CanvasAngularLand } from '../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication, ICanvasDestroyable } from '@appCanvasCommon/core/AbstractApplication';
import { combineLatest, Subject } from 'rxjs';
import { map, sample, takeUntil } from 'rxjs/operators';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { TodayLineDrawer } from '@appPlanApplication/timeline/today-line/TodayLineDrawer';
import { BackgroundDrawer } from '@appPlanApplication/timeline/BackgroundDrawer';
import { DestroyOptions, IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { MilestoneLineDrawer } from '@appPlanApplication/timeline/milestone-line/MilestoneLineDrawer';
import { AheadBehindDrawer } from '@appPlanApplication/timeline/ahead-behind/AheadBehindDrawer';
import { ActiveLineDrawer } from '@appPlanApplication/timeline/active-line/ActiveLineDrawer';
import { PullSpaceDrawer } from '@appPlanApplication/timeline/pull-space/PullSpaceDrawer';
import { ActiveSpaceDrawer } from '@appPlanApplication/timeline/active-space/ActiveSpaceDrawer';
import { TimelineTicketDrawer } from '@appPlanApplication/timeline/tickets/TimelineTicketDrawer';
import { PromisePeriodBackgroundDrawer } from '@appPlanApplication/timeline/promise-period/PromisePeriodBackgroundDrawer';
import { PromisePeriodHeaderDrawer } from '@appPlanApplication/timeline/promise-period/PromisePeriodHeaderDrawer';

/**
 * FancyTimeline
 * This class acts as a wrapper for each of the self-contained "drawer" elements that will draw on the timeline.
 * It also handles the destruction and creation based on the appropriate feature flag and user preference setting
 * until the new canvas-based timeline is made as the default.
 */
export class FancyTimeline implements ICanvasDestroyable {
	/**
	 * Helper method to retrieve the active line drawer. We use this so we can access specific API externally
	 * (like disabling the label at certain moments during exporting)
	 */
	get activeLine(): ActiveLineDrawer {
		return this._activeLineDrawer;
	}
	/**
	 * Helper method to retrieve the milestone line drawer. We use this so we can access specific API externally
	 * (like disabling the label at certain moments during exporting)
	 */
	get milestoneLine(): MilestoneLineDrawer {
		return this._milestoneLineDrawer;
	}
	/**
	 * Dispatched when destroy() is invoked. Cleans up internal subscriptions
	 * @private
	 */
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * Only required while we are maintaining old DOM-based timeline and new canvas-based timeline
	 * Used to determine current state of the view to determine if elements should be destroyed or created based on
	 * feature flag and user preference state.
	 * @private
	 */
	private _elementsInitialized: boolean = false;
	/**
	 * The class that is responsible for drawing the today line view.
	 * @private
	 */
	private _todayLineDrawer: TodayLineDrawer;
	/**
	 * The class that is responsible for drawing the active line view.
	 * @private
	 */
	private _activeLineDrawer: ActiveLineDrawer;
	/**
	 * The class that is responsible for drawing the timeline background
	 * @private
	 */
	private _backgroundDrawer: BackgroundDrawer;
	/**
	 * The class that is responsible for drawing the timeline milestone line
	 * @private
	 */
	private _milestoneLineDrawer: MilestoneLineDrawer;
	/**
	 * The class that is responsible for drawing the ahead/behind visual
	 * @private
	 */
	private _aheadBehindDrawer: AheadBehindDrawer;
	/**
	 * The class that is responsible for drawing the pull space visuals
	 * @private
	 */
	private _pullSpaceDrawer: PullSpaceDrawer;
	/**
	 * The class that is responsible for drawing the active space visuals
	 * @private
	 */
	private _activeSpaceDrawer: ActiveSpaceDrawer;
	/**
	 * The class that is responsible for drawing the timeline tickets
	 * @private
	 */
	private _timelineTicketDrawer: TimelineTicketDrawer;
	/**
	 * The class that is responsible for drawing the promise period background visual
	 * @private
	 */
	private _promisePeriodBackground: PromisePeriodBackgroundDrawer;
	/**
	 * The class that is responsible for drawing the promise period header element (that lives on the timeline layer)
	 * @private
	 */
	private _promisePeriodHeader: PromisePeriodHeaderDrawer;
	/**
	 * Creates a new instance of FancyTimeline.
	 * @param _canvasAngularLand - Interface to Angular-managed canvas state
	 * @param _application - The canvas application instance
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, private _application: AbstractApplication) {
		this.setupLayers();
		this.setupSubscribers();
	}
	/**
	 * Updates the timeline elements. Only processes updates when elements are properly initialized.
	 * @param delta - Time elapsed since last update
	 */
	public update(delta: number): void {
		if (!this._elementsInitialized) {
			return;
		}

		this._backgroundDrawer.update(delta);
		this._aheadBehindDrawer.update(delta);
		this._todayLineDrawer.update(delta);
		this._milestoneLineDrawer.update(delta);
		this._activeLineDrawer.update(delta);
		this._pullSpaceDrawer.update(delta);
		this._activeSpaceDrawer.update(delta);
		this._timelineTicketDrawer.update();
		this._promisePeriodBackground.update();
		this._promisePeriodHeader.update();
	}
	/**
	 * Forces a refresh of all timeline elements
	 */
	public draw(): void {
		if (!this._elementsInitialized) {
			return;
		}
		this._backgroundDrawer.draw();
		this._aheadBehindDrawer.draw();
		this._todayLineDrawer.draw();
		this._milestoneLineDrawer.draw();
		this._activeLineDrawer.draw();
		this._pullSpaceDrawer.draw();
		this._activeSpaceDrawer.draw();
		this._timelineTicketDrawer.draw();
		this._promisePeriodBackground.draw();
		this._promisePeriodHeader.draw();
	}
	/**
	 * Handles timeline resizing events by redrawing elements.
	 * @param width - New width of the canvas
	 * @param height - New height of the canvas
	 */
	public resize(width: number, height: number): void {
		if (!this._elementsInitialized) {
			return;
		}
		this.draw();
	}
	/**
	 * Cleans up resources when the timeline is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		this.destroyTimelineElements(opts);
		this._shutdownSubject$.next();
		this._shutdownSubject$.complete();

		this._canvasAngularLand = null;
		this._application = null;
	}
	/**
	 * Destroys timeline-specific elements and marks them as uninitialized.
	 * Called when feature flags or preferences change to disable the timeline.
	 * Can be removed once the canvas-based timeline is made the default.
	 */
	private destroyTimelineElements(opts?: IDestroyOptions): void {
		if (!this._elementsInitialized) {
			return;
		}
		this._backgroundDrawer.destroy(opts);
		this._aheadBehindDrawer.destroy(opts);
		this._todayLineDrawer.destroy(opts);
		this._milestoneLineDrawer.destroy(opts);
		this._activeLineDrawer.destroy(opts);
		this._pullSpaceDrawer.destroy(opts);
		this._activeSpaceDrawer.destroy(opts);
		this._timelineTicketDrawer.destroy(opts);
		this._promisePeriodBackground.destroy(opts);
		this._promisePeriodHeader.destroy(opts);
		this._elementsInitialized = false;
	}
	/**
	 * Initializes timeline elements
	 * Called when feature flags and preferences allow timeline creation.
	 * Can be removed once the canvas-based timeline is made the default.
	 */
	private setupTimeline(): void {
		this._backgroundDrawer = new BackgroundDrawer(this._canvasAngularLand, this._application);
		this._aheadBehindDrawer = new AheadBehindDrawer(this._canvasAngularLand, this._application);
		this._todayLineDrawer = new TodayLineDrawer(this._canvasAngularLand, this._application);
		this._milestoneLineDrawer = new MilestoneLineDrawer(this._canvasAngularLand, this._application);
		this._activeLineDrawer = new ActiveLineDrawer(this._canvasAngularLand, this._application);
		this._pullSpaceDrawer = new PullSpaceDrawer(this._canvasAngularLand, this._application);
		this._activeSpaceDrawer = new ActiveSpaceDrawer(this._canvasAngularLand, this._application);
		this._timelineTicketDrawer = new TimelineTicketDrawer(this._canvasAngularLand, this._application);
		this._promisePeriodBackground = new PromisePeriodBackgroundDrawer(
			this._canvasAngularLand,
			this._application
		);
		this._promisePeriodHeader = new PromisePeriodHeaderDrawer(this._canvasAngularLand, this._application);
		this._elementsInitialized = true;
		// BEGIN PLACEHOLDER: Replace as part of TP-6774
		this._application.camera.dispatchChanged();
		// END PLACEHOLDER
		this.resize(this._application.camera.view.width, this._application.camera.view.height);
	}
	/**
	 * Creates the necessary layers in the canvas scene graph for timeline elements.
	 */
	private setupLayers(): void {
		this._application.layerManager.addLayer(LayerPaths.BACKGROUND_TIMELINE);
		this._application.layerManager.addLayer(LayerPaths.UI_TIMELINE_ROOT);
	}
	/**
	 * Sets up subscriptions to monitor feature flags and user preferences.
	 * Timeline elements are created or destroyed based on these settings:
	 * - Modern timeline feature flag must be enabled (MODERN_TIMELINE_ENABLED) in Firebase
	 * - User must have opted to use the modern timeline (By explicitly enabling it under "Advanced Settings")
	 * - Timeline elements must not already exist
	 * Can be removed up once the canvas-view timeline is made the default.
	 */
	private setupSubscribers(): void {
		combineLatest([
			this._canvasAngularLand.featureFlagManager.getFlag$(DynamicFlagTypes.MODERN_TIMELINE_ENABLED),
			this._canvasAngularLand.userPreferenceStateService.useModernTimeline$,
		])
			.pipe(
				map(
					([hasModernTimeline, shouldUseModernTimeline]: [boolean, boolean]) =>
						hasModernTimeline && shouldUseModernTimeline && !this._elementsInitialized
				),
				takeUntil(this._shutdownSubject$)
			)
			.subscribe((shouldCreateTimeline: boolean) => {
				if (shouldCreateTimeline) {
					this.setupTimeline();
					return;
				}

				if (!shouldCreateTimeline && this._elementsInitialized) {
					this.destroyTimelineElements(DestroyOptions.includeChildren);
				}
			});

		/**
		 * TP-6927: Since the canvas is drawing the timeline, we need to listen for project data changes and project member changes
		 * and trigger a redraw to update the timeline. This handles the following cases:
		 * - When the authed project members primary role ID changes (role selector in navbar, or admin changes it, etc)
		 * - When the firebase project data changes (such as work days)
		 */
		combineLatest([
			this._canvasAngularLand.projectObject.object$,
			this._canvasAngularLand.projectWorkingDayService.changed$,
		])
			.pipe(takeUntil(this._shutdownSubject$), sample(this._canvasAngularLand.renderQueueWithCleanup$))
			.subscribe((data) => {
				this.draw();
			});
	}
}
